import { clsx } from 'clsx'
import { useState } from 'react'
import { DAYS, DAY_LABELS, DAY_SHORT, newId, newPromotion, promotionWhen, type DayKey, type Promotion } from '@sonder/shared'
import { Button } from './Button'
import { Collapsible } from './Collapsible'
import { DatePicker } from './DatePicker'
import { Field, Input, Textarea } from './Field'
import { Plus } from './icons'
import { TimeSelect } from './ScheduleBuilder'
import { Toggle } from './Toggle'

type Props = {
  value: Promotion[]
  onChange: (next: Promotion[]) => void
  /** Shows "Copy to…" on each promotion; the caller picks the other locations. */
  onCopy?: (promo: Promotion) => void
}

/**
 * Specials and promotions for one location: an "Add" prompt instead of empty day-by-day boxes. Each one says
 * what it is, which days (or one date) and what time. Opening hours stay in the ScheduleBuilder.
 */
export function PromotionsBuilder({ value, onChange, onCopy }: Props) {
  // Each promotion is a one-line summary until opened; one just added starts open.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set())
  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s)
      if (!next.delete(id)) next.add(id)
      return next
    })
  const update = (id: string, patch: Partial<Promotion>) => onChange(value.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  const add = () => {
    const p = newPromotion(newId().slice(0, 8))
    onChange([...value, p])
    setOpen((s) => new Set(s).add(p.id))
  }
  return (
    <div className="grid gap-4">
      {value.length === 0 && <p className="text-small text-dim">No specials or promotions yet.</p>}
      {value.map((p, i) => (
        <PromotionCard
          key={p.id}
          promo={p}
          index={i}
          open={open.has(p.id)}
          onToggle={() => toggle(p.id)}
          onChange={(patch) => update(p.id, patch)}
          onRemove={() => onChange(value.filter((x) => x.id !== p.id))}
          onCopy={onCopy && (() => onCopy(p))}
        />
      ))}
      <div>
        <Button variant="ghost" size="sm" arrow={false} icon={<Plus size={12} />} onClick={add}>
          Add a special or promotion
        </Button>
      </div>
    </div>
  )
}

type CardProps = { promo: Promotion; index: number; open: boolean; onToggle: () => void; onChange: (patch: Partial<Promotion>) => void; onRemove: () => void; onCopy?: () => void }

function PromotionCard({ promo: p, index, open, onToggle, onChange, onRemove, onCopy }: CardProps) {
  const title = p.name.trim() || `Promotion ${index + 1}`
  const toggleDay = (d: DayKey) => onChange({ days: p.days.includes(d) ? p.days.filter((x) => x !== d) : DAYS.filter((x) => x === d || p.days.includes(x)) })
  return (
    <Collapsible
      label={title}
      open={open}
      onToggle={onToggle}
      summary={
        <span className="grid min-w-0 gap-0.5">
          <span className="text-body font-medium text-ink">{title}</span>
          <span className="text-small text-dim tabular-nums">{promotionWhen(p)}</span>
          {!open && p.details.trim() && <span className="line-clamp-1 text-small text-charcoal">{p.details}</span>}
        </span>
      }
      actions={
        <>
          {onCopy && (
            <Button variant="link" size="sm" arrow={false} onClick={onCopy}>
              Copy to…
            </Button>
          )}
          <Button variant="danger-link" size="sm" arrow={false} onClick={onRemove}>
            Remove
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input value={p.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Taco Tuesday, Ladies’ night, Wine dinner" autoComplete="off" />
      </Field>
      <Field label="What’s the offer" hint="Prices, what’s included, any fine print.">
        <Textarea rows={2} autoGrow value={p.details} onChange={(e) => onChange({ details: e.target.value })} placeholder="$2 tacos and $5 margaritas, dine-in only" />
      </Field>

      <fieldset className="grid gap-3">
        <legend className="mb-3 text-eyebrow uppercase tracking-eyebrow font-medium text-ink">When</legend>
        <div role="radiogroup" aria-label="How often" className="flex flex-wrap gap-2">
          {(
            [
              ['weekly', 'Every week'],
              ['once', 'One date'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={p.repeat === v}
              onClick={() => onChange({ repeat: v })}
              className={clsx('min-h-8 border px-3 text-small transition-colors', p.repeat === v ? 'border-ink bg-ink text-paper' : 'border-gray-300 text-charcoal hover:border-ink')}
            >
              {label}
            </button>
          ))}
        </div>
        {p.repeat === 'weekly' ? (
          // All seven days on one line: equal columns on a phone, fixed-width buttons from sm up.
          <div className="grid grid-cols-7 gap-1 sm:flex sm:flex-wrap sm:gap-1.5" aria-label="Days it runs" role="group">
            {DAYS.map((d) => {
              const on = p.days.includes(d)
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  aria-label={DAY_LABELS[d]}
                  onClick={() => toggleDay(d)}
                  className={clsx('min-h-9 border text-small transition-colors sm:w-12', on ? 'border-ink bg-ink text-paper' : 'border-gray-300 text-charcoal hover:border-ink')}
                >
                  {DAY_SHORT[d]}
                </button>
              )
            })}
          </div>
        ) : (
          <Field label="Date" labelHidden>
            <DatePicker value={p.date} onChange={(date) => onChange({ date })} className="sm:max-w-60" />
          </Field>
        )}
      </fieldset>

      <div className="grid gap-3">
        <Toggle
          size="sm"
          checked={p.allDay}
          onChange={(allDay) => onChange(allDay ? { allDay } : { allDay, start: p.start ?? '16:00', end: p.end ?? '19:00' })}
          label="All day"
        />
        {!p.allDay && (
          <div className="flex flex-wrap items-center gap-2">
            <TimeSelect label={`${title} starts`} value={p.start ?? '16:00'} onChange={(start) => onChange({ start })} />
            <span className="text-dim" aria-hidden="true">
              –
            </span>
            <TimeSelect label={`${title} ends`} value={p.end ?? '19:00'} onChange={(end) => onChange({ end })} />
          </div>
        )}
      </div>
    </Collapsible>
  )
}
